import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, CalendarDays, Search, Timer, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InterventionDialog } from "@/components/forms";
import { ActivityZone } from "@/components/dashboard/ActivityZone";
import { PriorityZone } from "@/components/dashboard/PriorityZone";
import { BillingZone } from "@/components/dashboard/BillingZone";
import { SecondaryZone } from "@/components/dashboard/SecondaryZone";
import { WorkshopStrip } from "@/components/dashboard/WorkshopStrip";
import { buildPriorities } from "@/components/dashboard/priorities";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ActiveTimers = ({ timers }) =>
  timers.length > 0 && (
    <div className="space-y-2" data-testid="dashboard-active-timers">
      {timers.map((t) => (
        <Link key={t.intervention_id} to={`/interventions/${t.intervention_id}?chrono=1`} data-testid={`active-timer-${t.intervention_id}`}
          className="flex min-h-[64px] items-center gap-3 rounded-2xl border border-cyan-400/50 bg-cyan-400/10 p-4">
          <Timer className={`h-6 w-6 text-cyan-300 ${t.status === "paused" ? "" : "animate-pulse"}`} />
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-cyan-300">{t.status === "paused" ? "Chronomètre en pause" : "Chronomètre en cours"}</p>
            <p className="truncate font-medium text-white">{t.title} <span className="text-slate-400">· {t.user_name}</span></p>
          </div>
          <span className="text-sm font-medium text-cyan-200">Ouvrir</span>
        </Link>
      ))}
    </div>
  );

const ClientSearch = () => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  return (
    <form onSubmit={(e) => { e.preventDefault(); nav(`/clients${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`); }} className="relative w-full sm:w-64" data-testid="quick-find-client">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client…" className="h-11 pl-9" data-testid="quick-find-client-input" />
    </form>
  );
};

const Header = ({ name, onQuick }) => {
  const nav = useNavigate();
  const day = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Paris" });
  return (
    <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p className="text-sm text-slate-400" data-testid="dashboard-date">{day.charAt(0).toUpperCase() + day.slice(1)}</p>
        <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Bonjour{name ? `, ${name}` : ""}</h1>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <ClientSearch />
        <div className="grid grid-cols-[1fr_auto_auto] gap-2">
          <Button onClick={() => nav("/interventions/nouvelle")} className="dt-glow h-11 font-semibold" data-testid="quick-wizard"><Plus className="mr-1 h-4 w-4" />Nouvelle intervention</Button>
          <Button onClick={onQuick} variant="secondary" className="h-11" title="Création rapide" data-testid="quick-new-intervention"><Zap className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Rapide</span></Button>
          <Button variant="outline" className="h-11" onClick={() => nav("/calendrier")} title="Planning" data-testid="quick-planning"><CalendarDays className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Planning</span></Button>
        </div>
      </div>
    </header>
  );
};

export default function Home() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [v3, setV3] = useState(null);
  const [backup, setBackup] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/dashboard/v3").then((r) => setV3(r.data)).catch(() => setV3(null)); }, []);
  useEffect(() => { if (isAdmin) api.get("/backups/status").then((r) => setBackup(r.data)).catch(() => setBackup(null)); }, [isAdmin]);
  const c = data?.counts || {};
  const s = data?.stats || {};
  const { urgent, rest } = buildPriorities(data, v3, backup, isAdmin);

  return (
    <div className="space-y-5" data-testid="dashboard">
      <Header name={user?.name?.split(" ")[0]} onQuick={() => setOpen(true)} />
      <ActiveTimers timers={data?.active_timers || []} />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <div className="space-y-5 lg:col-span-3"><ActivityZone c={c} today={data?.interventions_today} /><WorkshopStrip c={c} /></div>
        <div className="space-y-5 lg:col-span-2">
          <PriorityZone urgent={urgent} rest={rest} loaded={!!data} />
          {isAdmin && <BillingZone c={c} s={s} v3={v3} />}
        </div>
      </div>
      <SecondaryZone c={c} s={s} v3={v3} backup={backup} />
      <InterventionDialog open={open} onOpenChange={setOpen} initial={{}} onSaved={load} />
    </div>
  );
}
